/**
 * The one mount point. Put this in the shell of an application and every
 * screen has it, without a single screen doing anything for it (FR-1).
 *
 * Four inputs and not one more. The module carries no address, no token, no
 * knowledge of the routing and no assumption about the application it hangs
 * inside; all four of those are handed in here. That is what makes the second
 * adoption cost one vendor run, one grant, one mount point and one
 * configuration value rather than a port (FR-23a).
 *
 * It is deliberately not a row in the navigation. The maintenance application
 * calls its equipment ticket "Report a fault" and puts it at the top of that
 * same list, so a feedback row in that menu is a row that can be read wrong.
 * A floating launcher is not a menu item and cannot be confused with one.
 */
import { Button } from "@depicomnlbv/platform-ui";
import { useEffect, useId, useRef, useState } from "react";

import { Panel } from "./Panel";
import { copy } from "./copy";
import { FeedbackProvider } from "./context";
import type { Rasteriser } from "./screenshot";
import { Icon } from "./ui";
import "./feedback.css";

export interface FeedbackLauncherProps {
  /** Where the host mounted the module's router, for instance "/api/feedback". */
  apiBase: string;
  /** The host's own token. Asked for per call, so a refresh reaches the module. */
  getToken: () => Promise<string> | string;
  /** The path the reader is on. The host knows it; the module never guesses. */
  currentScreen: string;
  /** The commit this bundle was built from. Without it half the context is empty. */
  build: string;
  /**
   * What paints the page for a screenshot (FR-21). Optional, and a host has no
   * reason to pass it: `html2canvas` is the default and the reason for the seam
   * is that a rasteriser can then be exercised without a browser.
   */
  rasterise?: Rasteriser;
}

export function FeedbackLauncher({
  apiBase,
  getToken,
  currentScreen,
  build,
  rasterise,
}: FeedbackLauncherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const launcherId = useId();
  const wasOpen = useRef(false);

  // Focus goes back where it came from, in the commit that removes the panel
  // rather than in a callback that may or may not run before the reader
  // reaches for the keyboard again. Dropping it on the body would make a
  // keyboard reader start again from the top of the page, which on the board
  // is fourteen navigation rows away from where he was (FR-18).
  useEffect(() => {
    if (wasOpen.current && !isOpen) document.getElementById(launcherId)?.focus();
    wasOpen.current = isOpen;
  }, [isOpen, launcherId]);

  return (
    <FeedbackProvider
      apiBase={apiBase}
      getToken={getToken}
      currentScreen={currentScreen}
      build={build}
      rasterise={rasterise}
    >
      <div className="pf-root">
        <div className="pf-launcher">
          <Button
            id={launcherId}
            variant="primary"
            aria-expanded={isOpen}
            onPress={() => setIsOpen((open) => !open)}
          >
            <Icon name="bubble" />
            {copy.launcher.label}
          </Button>
        </div>
        {isOpen ? <Panel onClose={() => setIsOpen(false)} /> : null}
      </div>
    </FeedbackProvider>
  );
}
